import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Divider,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  AudioFile as AudioFileIcon,
  FolderOpen as FolderOpenIcon,
} from '@mui/icons-material';
import DialogWindow from '../DialogWindow';

interface UploadTypeSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectProjectFiles: () => void;
  onSelectStems: () => void;
}

interface UploadTypeCardProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  accentColor: string; // resolved CSS colour
  onClick: () => void;
}

const UploadTypeCard: React.FC<UploadTypeCardProps> = ({
  icon,
  title,
  subtitle,
  accentColor,
  onClick,
}) => (
  <Card
    variant="outlined"
    sx={{
      flex: 1,
      borderColor: 'divider',
      borderWidth: 1.5,
      borderRadius: 3,
      transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.1s',
      '&:hover': {
        borderColor: accentColor,
        boxShadow: `0 0 0 1px ${accentColor}, 0 8px 24px ${alpha(
          accentColor,
          0.15
        )}`,
        transform: 'translateY(-2px)',
      },
      '&:active': { transform: 'translateY(0)' },
    }}
  >
    <CardActionArea
      onClick={onClick}
      sx={{ height: '100%', borderRadius: 'inherit' }}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 2,
          py: 4,
          px: 3,
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '20px',
            bgcolor: alpha(accentColor, 0.12),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s',
            'button:hover &': { bgcolor: alpha(accentColor, 0.2) },
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" lineHeight={1.5}>
            {subtitle}
          </Typography>
        </Box>
      </CardContent>
    </CardActionArea>
  </Card>
);

const UploadTypeSelectionDialog: React.FC<UploadTypeSelectionDialogProps> = ({
  open,
  onClose,
  onSelectProjectFiles,
  onSelectStems,
}) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const secondaryColor = theme.palette.secondary.main;

  const handleProjectFiles = () => {
    onClose();
    onSelectProjectFiles();
  };

  const handleStems = () => {
    onClose();
    onSelectStems();
  };

  return (
    <DialogWindow
      open={open}
      onClose={onClose}
      title="Upload Projects"
      maxWidth="md"
      fullWidth
      hideExpand
    >
      <Box sx={{ p: 3, pb: 4 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, textAlign: 'center' }}
        >
          What would you like to upload?
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'stretch',
            gap: 0,
          }}
        >
          <UploadTypeCard
            icon={<FolderOpenIcon sx={{ fontSize: 36, color: primaryColor }} />}
            title="Project Files"
            subtitle={'Ableton, Logic Pro,\nPro Tools, and more'}
            accentColor={primaryColor}
            onClick={handleProjectFiles}
          />

          {/* OR divider */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'row', sm: 'column' },
              alignItems: 'center',
              justifyContent: 'center',
              alignSelf: 'stretch',
              mx: { xs: 0, sm: 1.5 },
              my: { xs: 1.5, sm: 0 },
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                flexDirection: { xs: 'row', sm: 'column' },
              }}
            >
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  flex: 1,
                  minHeight: 0,
                }}
              />
              <Divider
                flexItem
                sx={{ display: { xs: 'flex', sm: 'none' }, flex: 1 }}
              />
            </Box>
            <Typography
              variant="overline"
              sx={{
                color: 'text.disabled',
                fontWeight: 700,
                letterSpacing: '0.1em',
                fontSize: '0.65rem',
                lineHeight: 1,
                mx: { xs: 1.5, sm: 0 },
                my: { xs: 0, sm: 1 },
                flexShrink: 0,
              }}
            >
              OR
            </Typography>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                flexDirection: { xs: 'row', sm: 'column' },
              }}
            >
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  flex: 1,
                  minHeight: 0,
                }}
              />
              <Divider
                flexItem
                sx={{ display: { xs: 'flex', sm: 'none' }, flex: 1 }}
              />
            </Box>
          </Box>

          <UploadTypeCard
            icon={
              <AudioFileIcon sx={{ fontSize: 36, color: secondaryColor }} />
            }
            title="Project Stems"
            subtitle="A folder of exported audio files"
            accentColor={secondaryColor}
            onClick={handleStems}
          />
        </Box>
      </Box>
    </DialogWindow>
  );
};

export default UploadTypeSelectionDialog;
